/* 劈裂动画 */
import { Canvas, dep, DisplayObject, Filter, getDefaults } from '@tencent/alloy-renderer';
import { PresetSubtype } from '@tencent/slide-sdk-model';
import { AnimFilters, Action } from '../../anim-constants';
import { IAnimFilterOptions } from '../../anim-interface';

export default class SplitFilter extends Filter {
    public static defaults: IAnimFilterOptions = {
        rate: 0,
        direction: PresetSubtype.Bottom,
        action: Action.ENTR,
    };

    @dep public rate: number;
    @dep public direction = PresetSubtype.Bottom;
    @dep public action = Action.ENTR;

    public forbidWebgl = true;
    public type = AnimFilters.SPLIT;

    public constructor(options: Partial<IAnimFilterOptions>) {
        super();
        const defaults = getDefaults<typeof SplitFilter>(this);
        this.rate = options.rate ?? defaults.rate;
        this.direction = options.direction ?? defaults.direction;
        this.action = options.action ?? defaults.action;
    }

    public apply(obj: DisplayObject, canvas: Canvas) {
        switch (this.direction) {
            case PresetSubtype.HorizontalIn:
                this.applyHorizontalIn(canvas);
                break;
            case PresetSubtype.HorizontalOut:
                this.applyHorizontalOut(canvas);
                break;
            case PresetSubtype.VerticalOut:
                this.applyVerticalOut(canvas);
                break;
            default:
                this.applyVerticalIn(canvas);
        }
    }


    /**
     * 上下向中央收缩
     * @param canvas Canvas
     */
    protected applyHorizontalIn = (canvas: Canvas) => {
        const { width, height, x, y } = this.getCanvasSizeAndPosition(canvas);
        if (this.isEntr) {
            const filterHeight = (1 - this.rate) * height;
            const filterY =  y + ((height - filterHeight) / 2);
            canvas.clearRect(x, filterY, width, filterHeight);
        } else {
            const offset = height * (1 - this.rate) / 2;
            const filterY1 = y - offset;
            const filterY2 = y + offset + (height / 2);
            canvas.clearRect(x, filterY1, width, height / 2);
            canvas.clearRect(x, filterY2, width, height / 2);
        }
    };

    /**
     * 中央向上下展开
     * @param canvas Canvas
     */
    protected applyHorizontalOut = (canvas: Canvas) => {
        const { width, height, x, y } = this.getCanvasSizeAndPosition(canvas);
        if (this.isEntr) {
            const offset = height * this.rate / 2;
            const filterY1 = y - offset;
            const filterY2 = y + offset + (height / 2);
            canvas.clearRect(x, filterY1, width, height / 2);
            canvas.clearRect(x, filterY2, width, height / 2);
        } else {
            const filterHeight = this.rate * height;
            const filterY =  y + ((height - filterHeight) / 2);
            canvas.clearRect(x, filterY, width, filterHeight);
        }
    };

    /**
     * 左右向中央收缩
     * @param canvas Canvas
     */
    protected applyVerticalIn = (canvas: Canvas) => {
        const { width, height, x, y } = this.getCanvasSizeAndPosition(canvas);
        if (this.isEntr) {
            const filterWidth = (1 - this.rate) * width;
            const filterX = x + ((width - filterWidth) / 2);
            canvas.clearRect(filterX, y, filterWidth, height);
        } else {
            const offset = width * (1 - this.rate) / 2;
            const filterX1 = x - offset;
            const filterX2 = x + offset + (width / 2);
            canvas.clearRect(filterX1, y, width / 2, height);
            canvas.clearRect(filterX2, y, width / 2, height);
        }
    };


    /**
     * 中央向左右展开
     * @param canvas Canvas
     */
    protected applyVerticalOut = (canvas: Canvas) => {
        const { width, height, x, y } = this.getCanvasSizeAndPosition(canvas);
        if (this.isEntr) {
            const offset = width * this.rate / 2;
            const filterX1 = x - offset;
            const filterX2 = x + offset + (width / 2);
            canvas.clearRect(filterX1, y, width / 2, height);
            canvas.clearRect(filterX2, y, width / 2, height);
        } else {
            const filterWidth = this.rate * width;
            const filterX =  x + ((width - filterWidth) / 2);
            canvas.clearRect(filterX, y, filterWidth, height);
        }
    };

    protected getCanvasSizeAndPosition = (canvas: Canvas) => {
        const { width, height, bounds } = canvas;
        const { x, y } = bounds;
        return { width, height, x, y };
    };

    protected get isEntr() {
        return this.action === Action.ENTR;
    }
}
